import {
  isPositiveNumber,
  isDefinedArray,
  isDefinedString
} from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { observable, runInAction, computed } from "mobx";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import getKhattabatMatchingStore from "../matching-stores";
import getKhattabat101MatchStore from ".";
import { TMatchDetail, TOneOOneMEnablementInformation } from "../../../Types";
import getKhattabatFilterStore from "../filter-store";
import { apiGetOneOOneMatchesConversations } from "../../../api/khattbat-api/api-call-get-one-o-one-matches-conversations";
import getCoreTimingStore from "../../core/core-timing-store";
import getKhattabatStore from "..";
import { apiCallSetOneOOneMEnablement } from "../../../api/khattbat-api/api-call-set-match-conversation-enablement";
import getKhattabatSelectsStore from "../selects-store";
import { apiCallIgnoreOneOOneM } from "../../../api/khattbat-api/api-call-ignore-one-o-one-m";
import getKhattabatSharedMessagingStore from "../messaging-stores/conversation-messaging-store";
import getKhattabat101MMessagingStore from "./101m-messaging-store";

class Khattabat101MGeneralStore {
  isMatchesIdsSelectedAll = observable.box<boolean>(true);
  messageToSendToCreated101M = observable.box<string>("");
  loadedMatchesIDs = observable.box<number[]>([]);
  clientID = observable.box<number>(0);

  setClientConversations = (updatedConversations: TMatchDetail[]) => {
    runInAction(() => {
      const currentIDs = this.loadedMatchesIDs.get();
      updatedConversations.forEach((matchDetail) => {
        getKhattabat101MatchStore(matchDetail.matchID).setMatchData(
          matchDetail
        );
        if (!currentIDs.includes(matchDetail.matchID)) {
          currentIDs.push(matchDetail.matchID);
        }
      });
      this.loadedMatchesIDs.set(currentIDs);
    });
  };

  resetClientConversations = () => {
    runInAction(() => {
      this.loadedMatchesIDs.set([]);
    });
  };

  setMessageToSendToCreated101M = (message: string) => {
    runInAction(() => {
      this.messageToSendToCreated101M.set(message.trim());
    });
  };

  toggleIsMatchesIdsSelectedAll = () => {
    const currentState = this.isMatchesIdsSelectedAll.get();
    runInAction(() => {
      this.isMatchesIdsSelectedAll.set(!currentState);
    });
  };

  resetSelectAll = () => {
    runInAction(() => {
      this.isMatchesIdsSelectedAll.set(false);
    });
  };

  canOpenOneOOneMConversations = computed(() => {
    return (
      isPositiveNumber(
        getKhattabatMatchingStore().currentClientInformation.get().userId
      ) &&
      isDefinedArray(getKhattabatMatchingStore().matchesIds.get()) &&
      isDefinedString(this.messageToSendToCreated101M.get()) &&
      isDefinedArray(getKhattabatMatchingStore().selectedMatchesIds.get())
    );
  });

  getAllAvailableMatchesIds = () => {
    return this.loadedMatchesIDs.get();
  };

  getMatches = (): number[] => {
    if (getKhattabatFilterStore().searching1o1MMatches.get()) {
      return getKhattabatFilterStore().filtered101MArray.get();
    }
    return this.loadedMatchesIDs.get();
  };

  orderedMatchIDs = computed((): number[] => {
    const matchIDs = this.getMatches();
    if (getKhattabatFilterStore().searching1o1MMatches.get()) {
      return matchIDs;
    }
    return orderBy(
      matchIDs,
      [
        (matchID) => {
          const conversationID =
            getKhattabat101MatchStore(matchID).conversationID.get();
          return getKhattabatSharedMessagingStore(conversationID).messages.get()
            .length;
        }
      ],
      "asc"
    );
  });

  getMatchesConversations = async () => {
    this.resetClientConversations();

    const { userId } =
      getKhattabatMatchingStore().currentClientInformation.get();
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", true);
    const { status, code, data } = await apiGetOneOOneMatchesConversations({
      shouldUpdateUnread: false
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", false);
    if (status === "OK") {
      runInAction(() => {
        this.clientID.set(userId);
      });
      this.setClientConversations(data);
      getCoreTimingStore().initializeKhattabatPageMonitoring(
        "KHATTABAT_MATCHING_RESULTS"
      );
      getKhattabatFilterStore().update1o1MFilter();
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUnreadMatchesConversations = async () => {
    const { status, code, data } = await apiGetOneOOneMatchesConversations({
      shouldUpdateUnread: true
    });
    if (status === "OK") {
      this.setClientConversations(data);
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getBasicMatchInformation = (matchIDs: number[]) => {
    return matchIDs.map((matchID) => {
      return {
        alkhattabaUUID: getKhattabatStore().alkhattabaUUID.get(),
        userUUID: getKhattabat101MatchStore(matchID).matchUUID.get(),
        userID: matchID
      };
    });
  };

  removeMatchUser = (matchIds: number[]) => {
    runInAction(() => {
      this.loadedMatchesIDs.set(
        this.loadedMatchesIDs
          .get()
          .filter((matchID) => !matchIds.includes(matchID))
      );
    });
  };

  updateClientOpenedOneOOneMConversations = () => {
    this.loadedMatchesIDs.get().forEach(async (matchID) => {
      const { conversationID } = getKhattabat101MatchStore(matchID);
      const { updateOneOOneMMessages } =
        getKhattabat101MMessagingStore(matchID);
      const isOpened =
        getKhattabatSharedMessagingStore(
          conversationID.get()
        ).isOpened.get() === "OPENED";
      if (isOpened) {
        await updateOneOOneMMessages({ shouldLoad: false });
      }
    });
  };
  disableEnableMatchConversationDatabase = async ({
    matchIDs,
    enableAllConversations,
    disableAllConversations
  }: {
    matchIDs: number[];
    enableAllConversations: boolean;
    disableAllConversations: boolean;
  }) => {
    let oneOOneMEnablementInformation: TOneOOneMEnablementInformation = [];
    matchIDs.forEach((matchID) => {
      const { alkhattabaUUID, userUUID } = this.getBasicMatchInformation([
        matchID
      ])[0];
      const oldState =
        getKhattabat101MatchStore(matchID).matchConvesationIsDisabled.get();
      const newIsBlockedState = enableAllConversations
        ? 0
        : disableAllConversations
          ? 1
          : oldState
            ? 0
            : 1;
      oneOOneMEnablementInformation.push({
        alkhattabaUUID,
        userUUID,
        newIsBlockedState,
        userID: matchID
      });
    });
    const { status, code } = await apiCallSetOneOOneMEnablement({
      oneOOneMEnablementInformation
    });
    if (status === "OK") {
      matchIDs.forEach((matchID) => {
        const oldState = disableAllConversations
          ? false
          : enableAllConversations
            ? true
            : getKhattabat101MatchStore(
                matchID
              ).matchConvesationIsDisabled.get();
        getKhattabat101MMessagingStore(
          matchID
        ).updateMatchConversationEnablementState({ oldState });
        getKhattabatFilterStore().update1o1MFilter();
      });
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  ignoreMatchConversationDatabase = async ({
    matchIDs
  }: {
    matchIDs: number[];
  }) => {
    const matchesInformation = this.getBasicMatchInformation(matchIDs);
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", true);
    const { status, code } = await apiCallIgnoreOneOOneM({
      matchesInformation
    });
    getCoreUIStore().setIsLoading("KHATTABAT_MATCHING_RESULTS", false);
    if (status === "OK") {
      runInAction(() => {
        matchIDs.forEach((matchID) => {
          getKhattabat101MatchStore(matchID).matchIsSlackAlertsEnabled.set(
            false
          );
        });
      });
      this.removeMatchUser(matchIDs);
      getKhattabatSelectsStore().resetSelectedConversations();
      getKhattabatFilterStore().update1o1MFilter();
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}

export const getKhattabat101MGeneralStore = memoize(
  () => new Khattabat101MGeneralStore(),
  () => 1
);
export default getKhattabat101MGeneralStore;
